// Standalone chrome (Nav + Footer) extracted from _v02_editorial.jsx so it
// can be mounted on interior portal pages, not just the homepage.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#fbfaf7', fg: '#0f0f0f', muted: '#6b6660',
  border: '#e6e1d6', accent: '#a8341c', accentFg: '#ffffff',
  inputBg: '#fff', radius: 2,
  tintedBg: '#f1ece0',
  font: '"Inter", system-ui, sans-serif',
  serif: '"Source Serif 4", Georgia, serif',
  display: '"Playfair Display", "Times New Roman", serif',
  mono: '"IBM Plex Mono", monospace',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navItems = [
  { label: 'Ratgeber',     href: '/ratgeber' },
  { label: 'Photovoltaik', href: '/photovoltaik' },
  { label: 'Wärmepumpe',   href: '/wärmepumpe' },
  { label: 'Förderung',    href: '/förderung-finder' },
  { label: 'Marktanalyse', href: '/marktanalyse' },
  { label: 'Forum',        href: '/forum' },
];

export function V02EditorialNav({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <>
      <div style={{
        padding: isMobile ? '14px 16px' : '20px 56px',
        borderBottom: `2px solid ${theme.fg}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: theme.bg, color: theme.fg, fontFamily: theme.font,
        position: 'relative',
      }}>
        <Link href="/" style={{ fontFamily: '"Playfair Display", "Times New Roman", serif', fontSize: isMobile ? 24 : 30, fontWeight: 900, letterSpacing: '-0.02em', fontStyle: 'italic', color: 'inherit', textDecoration: 'none' }}>{brand}</Link>
        {!isMobile && (
          <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.muted, fontFamily: '"IBM Plex Mono", monospace' }}>
            Sonntag · 26. April 2026 · Ausgabe Nº 412
          </div>
        )}
        {isMobile && (
          <button
            aria-label="Menü"
            onClick={() => setIsMenuOpen(o => !o)}
            style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
          >
            <HamburgerIcon open={isMenuOpen} />
          </button>
        )}
        {isMobile && isMenuOpen && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: theme.bg, borderBottom: `2px solid ${theme.fg}`,
            padding: '16px', display: 'flex', flexDirection: 'column', gap: 12,
            zIndex: 50,
          }}>
            {navItems.map(l => (
              <Link key={l.label} href={l.href} style={{
                color: theme.fg, textDecoration: 'none',
                fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600,
              }}>{l.label}</Link>
            ))}
          </div>
        )}
      </div>
      {!isMobile && (
        <div style={{
          padding: '8px 56px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', gap: 28, fontSize: 12, color: theme.muted, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600,
          background: theme.bg, fontFamily: theme.font,
        }}>
          {navItems.map(l => <Link key={l.label} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>{l.label}</Link>)}
        </div>
      )}
    </>
  );
}

export function V02EditorialFooter({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{
      borderTop: `2px solid ${theme.fg}`,
      padding: isMobile ? '24px 16px' : '36px 56px',
      background: theme.bg,
      color: theme.fg,
      fontFamily: theme.font,
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
        gap: isMobile ? 24 : 40, alignItems: 'start',
      }}>
        <div style={{
          fontFamily: theme.display, fontSize: isMobile ? 20 : 22,
          fontWeight: 900, fontStyle: 'italic', letterSpacing: '-0.02em',
        }}>
          {brand}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 16 : 28,
          fontFamily: theme.mono, fontSize: 11, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: theme.muted, fontWeight: 600,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Link href="/über-uns" style={{ color: 'inherit', textDecoration: 'none' }}>Über uns</Link>
            <Link href="/impressum" style={{ color: 'inherit', textDecoration: 'none' }}>Impressum</Link>
            <Link href="/kontakt" style={{ color: 'inherit', textDecoration: 'none' }}>Kontakt</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Link href="/datenschutz" style={{ color: 'inherit', textDecoration: 'none' }}>Datenschutz</Link>
            <Link href="/agb" style={{ color: 'inherit', textDecoration: 'none' }}>AGB</Link>
            <Link href="/widerruf" style={{ color: 'inherit', textDecoration: 'none' }}>Widerruf</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span>{`© ${new Date().getFullYear()}`}</span>
            <span>Ausgabe Nº 412</span>
            <span>Sonntag · 26.04.</span>
          </div>
        </div>
      </div>
      <div style={{
        marginTop: isMobile ? 20 : 28, paddingTop: 20,
        borderTop: `1px solid ${theme.border}`,
        fontFamily: theme.serif, fontStyle: 'italic',
        fontSize: 13, color: theme.muted,
      }}>
        Werbung als solche gekennzeichnet. Vermittlung kostenlos für Leser.
      </div>
    </footer>
  );
}
